import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorMessage, FormfieldHelpCta, Label } from 'shared';
import styles from './FormfieldTypeheadDawa.module.scss';

type State = {
	required?: boolean;
	disabled?: boolean;
	hasError?: boolean;
	isHidden?: boolean;
};

type Address = {
	roadName: string;
	roadNumber: string;
	postalCode: string;
	city: string;
	houseLetter?: string;
	floor?: string;
	sideDoor?: string;
};

type DawaFundType = 'vejnavn' | 'navngivenvejpostnummer' | 'husnummer' | 'adresse';

interface DawaOptionData {
	type: DawaFundType;
	id: string;
	titel: string;
	husnummerId?: string;
}

interface DawaSearchResponse {
	// response: {
	status: string;
	fund: DawaOptionData[];
	// };
}

interface AddressDetailsResponse {
	status: string;
	adresse: {
		id_lokalid: string;
		adressebetegnelse: string;
		etagebetegnelse?: string;
		doerbetegnelse?: string;
		husnummer: {
			husnummertekst: string;
			vejnavn: string;
			postnummer: {
				postnr: string;
				navn: string;
			};
			supplerendebynavn?: {
				navn?: string;
			};
		};
	};
}

interface Option {
	id: string;
	name: string;
	data?: DawaOptionData;
}

export interface FormfieldTypeheadDawaProps {
	className?: string;
	closeCtaText?: string;
	defaultValue?: string;
	id: string;
	helpCtaText?: string;
	label: string;
	name?: string;
	placeholder?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	register?: any;
	errorMessage?: string;
	state?: State;
	getAddress: (data: Address) => void;
}

const ADDRESS_API_URL = `${window.location.origin}/api/v3/openserviceproxy/adresser`;
const SEARCH_TYPE = 'adresser';
const DEBOUNCE_MS = 300;

const shouldContinueAddressSearch = (data: DawaOptionData | undefined, searchType: string): boolean => {
	return (
		data?.type === 'vejnavn' ||
		data?.type === 'navngivenvejpostnummer' ||
		(data?.type === 'husnummer' && searchType === 'adresser')
	);
};

const mapAddressDetails = ({ adresse }: AddressDetailsResponse): Address => {
	const { husnummer } = adresse;
	const [, , houseLetter] = /^(\d+)([A-Za-zÆØÅæøå]?)$/.exec(husnummer.husnummertekst ?? '') ?? [];

	return {
		roadName: husnummer.vejnavn,
		roadNumber: husnummer.husnummertekst,
		houseLetter: houseLetter || undefined,
		floor: adresse.etagebetegnelse || undefined,
		sideDoor: adresse.doerbetegnelse || undefined,
		postalCode: husnummer.postnummer.postnr,
		city: husnummer.postnummer.navn,
	};
};

const ariaLabel = (state: State, label: string) => {
	if (state?.['isHidden']) return label;
};

const ariaError = (state: State, type: string, id: string) => {
	if (state?.['hasError']) return type + id;
};

export const FormfieldTypeheadDawa: React.FC<FormfieldTypeheadDawaProps> = ({
	className,
	closeCtaText,
	state,
	id,
	helpCtaText,
	label,
	register,
	placeholder,
	name,
	defaultValue = '',
	errorMessage,
	getAddress,
}) => {
	const inputRef = useRef(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>();
	const abortRef = useRef<AbortController>();
	const skipSearchRef = useRef(false);
	const { required, disabled, hasError, isHidden } = state ?? {};
	const [address, setAddress] = useState<Address>();
	const [value, setValue] = useState(defaultValue);
	const [options, setOptions] = React.useState<Option[]>([]);

	useEffect(() => {
		void (defaultValue.length && getDawaAutocomplete(defaultValue, true));

		return () => {
			clearTimeout(debounceRef.current);
			abortRef.current?.abort();
		};
	}, []);

	useEffect(() => {
		getAddress(address);
	}, [address]);

	// Returns null when superseded by a newer request
	const searchAddresses = async (text: string): Promise<DawaOptionData[] | null> => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;

		try {
			const res = await fetch(`${ADDRESS_API_URL}/soeg?tekst=${encodeURIComponent(text)}`, {
				signal: controller.signal,
			});
			if (!res.ok) return [];
			const json = (await res.json()) as DawaSearchResponse;
			return json?.status === 'ok' ? json.fund ?? [] : [];
		} catch (e) {
			if ((e as Error).name === 'AbortError') return null;
			console.error(e);
			return [];
		}
	};

	const fetchAddressDetails = async (addressId: string) => {
		try {
			const res = await fetch(`${ADDRESS_API_URL}/${encodeURIComponent(addressId)}`);
			if (!res.ok) return;
			const json = (await res.json()) as AddressDetailsResponse;
			if (json?.status !== 'ok' || !json.adresse?.husnummer) return;
			setAddress(mapAddressDetails(json));
		} catch (e) {
			console.error(e);
		}
	};

	const getDawaAutocomplete = async (text: string, selectFirst = false, autoSelectSingle = false) => {
		if (!text.trim()) {
			setOptions([]);
			return;
		}

		const fund = await searchAddresses(text);
		if (!fund) return;

		if (selectFirst) {
			const first = fund.find((f) => f.type === 'adresse');
			if (first) await fetchAddressDetails(first.id);
			return;
		}

		const newOptions = fund.map((item) => ({ id: item.id, name: item.titel, data: item }));

		// Only final addresses; auto-continuing a single vejnavn could loop on the same result
		if (autoSelectSingle && newOptions.length === 1 && newOptions[0].data.type === 'adresse') {
			optionSelected(newOptions[0]);
			return;
		}

		setOptions(newOptions);
	};

	const debouncedGetDawaAutocomplete = (text: string, autoSelectSingle = false) => {
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => getDawaAutocomplete(text, false, autoSelectSingle), DEBOUNCE_MS);
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setValue(value);

		if (skipSearchRef.current) {
			skipSearchRef.current = false;
			return;
		}

		// Browser autofill fires a plain Event (no inputType) or 'insertReplacementText'
		const inputType = (event.nativeEvent as InputEvent | undefined)?.inputType;
		const isPasteOrAutofill =
			!inputType || inputType === 'insertFromPaste' || inputType === 'insertReplacementText';

		debouncedGetDawaAutocomplete(value, isPasteOrAutofill);
	};

	// Local state alone doesn't reach react-hook-form, so validation would see the typed text
	const setFieldValue = (newValue: string) => {
		setValue(newValue);
		if (!rest?.onChange) return;
		skipSearchRef.current = true;
		rest.onChange({ target: { name, value: newValue }, type: 'change' });
	};

	const handleBlur = () => {
		clearTimeout(debounceRef.current);

		const [only] = options;
		if (options.length === 1 && only.data?.type === 'adresse') {
			void fetchAddressDetails(only.data.id);
			setFieldValue(only.name);
		}

		setTimeout(() => {
			setOptions([]);
		}, 200);
	};

	const optionSelected = (option: Option) => {
		const data = option?.data;

		if (shouldContinueAddressSearch(data, SEARCH_TYPE)) {
			const next = `${option.name} `;
			setFieldValue(next);
			clearTimeout(debounceRef.current);
			void getDawaAutocomplete(next, false, true);
			inputRef.current?.focus();
			return;
		}

		if (data?.type === 'adresse') {
			void fetchAddressDetails(data.id);
		}

		setFieldValue(option?.name);
		setOptions([]);
		inputRef.current?.focus();
	};

	const renderOptions = () => {
		if (!options.length) return null;

		return (
			<ul className={classNames(styles.dawaAutocompleteSuggestions)}>
				{options.map((item) => {
					return (
						<li
							key={item.id}
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => optionSelected(item)}
							className={classNames(styles.dawaAutocompleteSuggestions_item)}
						>
							{item.name}
						</li>
					);
				})}
			</ul>
		);
	};

	const { ref = null, ...rest } = register && {
		...register(name, {
			onBlur: () => handleBlur(),
			onChange: (e) => handleChange(e),
			required: {
				value: required,
			},
		}),
	};

	return (
		<>
			<div
				className={classNames(
					styles.FormfieldString,
					className,
					{ [styles.hasError]: hasError },
					{ [styles.isDisabled]: disabled },
				)}
			>
				{!isHidden && (
					<div className={styles.FormfieldString_label}>
						<Label id={id} className={styles.FormfieldString_labelText} state={state}>
							{label}
						</Label>
						{helpCtaText && <FormfieldHelpCta closeText={closeCtaText} helpText={helpCtaText} />}
					</div>
				)}

				<input
					type="text"
					className={classNames(styles.FormfieldString_input, styles.Input___string, {
						[styles.hasError]: hasError,
					})}
					name={name}
					id={id}
					disabled={disabled}
					placeholder={placeholder}
					aria-label={ariaLabel(state, label)}
					aria-describedby={ariaError(state, 'text', id)}
					value={value}
					{...rest}
					ref={(e) => {
						ref(e);
						inputRef.current = e;
					}}
				/>
				{hasError && <ErrorMessage id={'text' + id}>{errorMessage}</ErrorMessage>}
				{renderOptions()}
			</div>
		</>
	);
};
